import { getTranslate } from 'react-localize-redux';
import {
  all,
  call,
  cancel,
  fork,
  put,
  select,
  spawn,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';

import { payment } from '../../../core/state';
import { GatewayPaymentAccount, PaymentAccount } from '../../Api';
import { paymentLogger } from '../../logger';
import {
  paymentApiServices,
  payPalClient,
  storageServices,
} from '../../services';
import * as stripeClient from '../../services/stripeServices';
import { basketActions, basketSelectors, paymentActions } from '..';
import { updateRouterState } from '../router/actions';
import { userActions, userSelectors } from '../user';

// #region HELPERS
const genNewCreditCardObject = (
  overrides: Partial<WebOrder.State.Payment>,
): WebOrder.State.Payment => ({
  PaymentAccountId: -1,
  CanRemoveFromCustomerView: false,
  CustomerId: null,
  Description: 'New_card',
  IsDefaultPaymentMethod: false,
  PaymentAccountType: 'CardPaymentAccount',
  PublicApiKey: '',
  SupportsSubmitOrderTips: true,
  TokenizationKey: '',
  UserId: 0,

  paymentId: 'new_card',
  displayOrder: 1,
  status: 'success',

  ...overrides,
});
// #endregion

// #region SAGAS
export function* cachePayments(data?: PaymentAccount[]) {
  yield call(storageServices.setPayments, data);
}

export function* getCachedPaymentsForCurrentUser() {
  const data: ReturnType<typeof storageServices.getPayments> = yield call(
    storageServices.getPayments,
  );

  if (data && data.length) {
    const user = userSelectors.getUser(yield select());
    if (user) {
      // if cashed data is for current user
      if (data.some((p) => p.UserId === user.UserId)) {
        return data;
      }
    }
  }

  return null;
}

export function* creditCardPaymentAdded(
  action: ReturnType<typeof paymentActions.creditCardPaymentAdded>,
) {
  try {
    // CLEAN CACHE
    yield spawn(cachePayments, undefined);

    const { payload } = action;
    const { paymentAccountId, cardNumber, hasConsentToSaveCard } = payload;
    const state: WebOrder.State = yield select();
    const translate = getTranslate(state.locale);
    const payments = payment.selectors.getPayments(state);
    const defaultPayment = Object.values(payments).find(
      (p) => p.IsDefaultPaymentMethod,
    );

    if (hasConsentToSaveCard) {
      // CONSTRUCT NEW PAYMENT
      // #region all this just to be in right order on screen ccccc
      const cardsDisplayOrder = Object.values(payments)
        .filter(
          (p) =>
            p.PaymentAccountType === 'CardPaymentAccount' &&
            p.paymentId !== 'new_card',
        )
        .map((p) => p.displayOrder);
      const lastDisplayOrder = Math.max(...cardsDisplayOrder, 0);
      const displayOrder = lastDisplayOrder
        ? lastDisplayOrder + 1
        : payments.new_card.displayOrder - 1;
      // #endregion

      const newPayment = genNewCreditCardObject({
        PaymentAccountId: paymentAccountId,
        CanRemoveFromCustomerView: true,
        Description: `${translate('Card')} ****${cardNumber.slice(
          cardNumber.length - 4,
        )}`,
        IsDefaultPaymentMethod: true,
        paymentId: `${paymentAccountId}`,
        displayOrder,
      });
      paymentLogger.debug(`CREDIT_CARD_ADDED `, paymentAccountId);
      // SET STATE
      yield put(paymentActions.setPayment(newPayment));
    }

    if (defaultPayment) {
      paymentLogger.debug(
        `CREDIT_CARD_ADDED CLEAR_DEFAULT`,
        defaultPayment.paymentId,
      );
      yield put(
        paymentActions.updatePayment({
          paymentId: defaultPayment.paymentId,
          IsDefaultPaymentMethod: false,
        }),
      );
    }
  } catch (error) {
    paymentLogger.debug(`CREDIT_CARD_ADDED ERROR`, error);
  }
}

export function* paymentRemoved(
  action: ReturnType<typeof paymentActions.paymentRemoved>,
) {
  try {
    // CLEAN CACHE
    yield spawn(cachePayments, undefined);

    const { paymentId } = action.payload;
    const state: WebOrder.State = yield select();
    const payments = payment.selectors.getPayments(state);

    const { [paymentId]: removed, ...rest } = payments;

    paymentLogger.debug('PAYMENT_REMOVED', removed.paymentId);

    // SET STATE
    if (removed.IsDefaultPaymentMethod) {
      const newPayments = Object.entries(rest).reduce((acc, [key, p], idx) => {
        if (idx === 0) {
          paymentLogger.debug('PAYMENT_REMOVED SET_DEFAULT', p.paymentId);
        }
        acc[key] = {
          ...p,
          IsDefaultPaymentMethod: idx === 0,
        };
        return acc;
      }, {} as WebOrder.State.Payments['items']);

      yield put(
        paymentActions.setState({
          status: 'success',
          items: newPayments,
          error: undefined,
        }),
      );
    } else {
      yield put(
        paymentActions.setState({
          status: 'success',
          items: rest,
          error: undefined,
        }),
      );
    }
  } catch (error) {
    paymentLogger.debug(`PAYMENT_REMOVED ERROR`, error);
  }
}

function* setDefaultSelected(
  action: ReturnType<typeof paymentActions.setDefaultSelected>,
) {
  try {
    const { paymentId } = action.payload;
    const state: WebOrder.State = yield select();
    const payments = payment.selectors.getPayments(state);

    paymentLogger.debug('SET_DEFAULT', paymentId);

    // SET STATE
    const nextPayments = Object.entries(payments).reduce((acc, [pId, p]) => {
      acc[pId] = {
        ...p,
        IsDefaultPaymentMethod: pId === paymentId,
      };
      return acc;
    }, {} as WebOrder.State.Payments['items']);

    yield put(
      paymentActions.setState({
        status: 'success',
        items: nextPayments,
        error: undefined,
      }),
    );
    yield put(basketActions.selectPayment({ paymentId }));

    // REFRESH CACHE IF THERE
    const sPayments: PaymentAccount[] = yield call(storageServices.getPayments);
    if (sPayments) {
      paymentLogger.debug('SET_DEFAULT_CACHE', paymentId);

      const nextStoragePayments = sPayments.map((p) => ({
        ...p,
        IsDefaultPaymentMethod: `${p.PaymentAccountId}` === paymentId,
      }));

      yield call(storageServices.setPayments, nextStoragePayments);
    }
  } catch (error) {
    paymentLogger.debug(`SET_DEFAULT ERROR`, error);
  }
}
// #endregion

// #region PAYPAL
export function* initPayPal() {
  try {
    const state: WebOrder.State = yield select();
    const paypal = payment.selectors.getPayment(state, {
      paymentId: 'paypal',
    });
    if (paypal && paypal.TokenizationKey) {
      try {
        paymentLogger.debug('PAYPAL INIT...');
        yield call(payPalClient.init, { token: paypal.TokenizationKey });
        yield put(
          paymentActions.updatePayment({
            paymentId: `paypal`,
            status: 'success',
          }),
        );
        paymentLogger.debug('PAYPAL INITIALIZED');
      } catch (error) {
        paymentLogger.debug('PAYPAL INIT FAILED');
        yield put(
          paymentActions.updatePayment({
            paymentId: `paypal`,
            status: 'error',
          }),
        );
      }
    }
  } catch (error) {
    paymentLogger.debug('PAYPAL ERROR', error);
  }
}
// #endregion
// #region STRIPE
function* initStripePaymentRequest() {
  try {
    const state = yield select();
    const stripe = payment.selectors.getPayment(state, {
      paymentId: 'stripe',
    });

    if (!stripe || stripe.status !== 'pending') {
      paymentLogger.debug('STRIPE no stripe or not pending', stripe);
      return;
    }

    let paymentPayload = basketSelectors.getStripePaymentRequestOptions(state);
    if (!paymentPayload) {
      let canContinue = false;
      while (!canContinue) {
        paymentLogger.debug('STRIPE WAITING 4 PAYMENT_REQUEST_OPTIONS');
        /*
        Action listeners to get Apple Pay and Google Pay out of infinite loading
        - SET_RESTAURANT_COLLECTION & SET_RESTAURANT_DELIVERY is listening for
        Restaurant summaries and route changes because not all routes have
        required {vrId, dlId} in them.
        - REMOVE_TOAST is listening for "Logged in" as a last ditch effort
        to update route and get Apple Pay and Google Pay out of infinite loading
        when refreshing the Checkout page.
        */
        yield take([
          'SET_RESTAURANT_COLLECTION',
          'SET_RESTAURANT_DELIVERY',
          'REMOVE_TOAST',
          updateRouterState,
        ]);

        paymentLogger.debug('STRIPE CHECKING 4 PAYMENT_REQUEST_OPTIONS');
        paymentPayload = basketSelectors.getStripePaymentRequestOptions(
          yield select(),
        );
        if (paymentPayload) {
          paymentLogger.debug(
            'STRIPE FOUND PAYMENT_REQUEST_OPTIONS',
            paymentPayload,
          );
          canContinue = true;
        }
      }
    }
    yield call(stripeClient.paymentRequest.create, paymentPayload);

    const canPay: Stripe.CanMakePaymentResult = yield call(
      stripeClient.paymentRequest.canMakePayment,
    );

    if (!canPay) {
      throw new Error('Not supported');
    }
    /*
    https://stripe.com/docs/js/payment_request/can_make_payment

    paymentRequest.canMakePayment only returns true on Safari 10.1+ (desktop and mobile),
    Chrome 61+ (desktop and mobile), and Microsoft Edge 16.16299+ when the customer has a
    card already saved with Apple Pay, Microsoft Pay, or their browser.

    In some situations, browsers will always resolve canMakePayment to true:

    Safari in a Private Browsing window
    Safari when the “Allow websites to check if Apple Pay is set up” preference is disabled
    */

    const description =
      // canPay === true ? '' :
      canPay.applePay ? 'Apple_pay' : 'Google_microsoft_pay';
    yield put(
      paymentActions.updatePayment({
        paymentId: 'stripe',
        status: 'success',
        Description: description,
      }),
    );
  } catch (error) {
    paymentLogger.debug('STRIPE PAYMENT_REQUEST FAILED', error);

    const state = yield select();
    const stripe = payment.selectors.getPayment(state, {
      paymentId: 'stripe',
    });
    if (stripe && stripe.IsDefaultPaymentMethod) {
      const payments = payment.selectors.getPayments(state);
      const paymentId = Object.values(payments)
        .filter((p) => p.paymentId !== 'stripe')
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .reduce((selected, p) => {
          // override default
          if (selected === 'new_card' && p.status === 'success') {
            return p.paymentId;
          }
          return selected;
        }, 'new_card');

      paymentLogger.debug('STRIPE SET_SELECTED_PAYMENT', paymentId);

      yield put(basketActions.selectPayment({ paymentId }));
    }

    throw error;
  }
}

export function* initStripe() {
  try {
    const state: WebOrder.State = yield select();
    const stripe = payment.selectors.getPayment(state, {
      paymentId: 'stripe',
    });

    if (stripe && stripe.PublicApiKey) {
      paymentLogger.debug('STRIPE INIT...');
      yield call(stripeClient.init, { token: stripe.PublicApiKey });

      yield call(initStripePaymentRequest);

      paymentLogger.debug('STRIPE INITIALIZED');
    }
  } catch (error) {
    paymentLogger.debug('STRIPE INIT FAILED', error);
    yield put(
      paymentActions.updatePayment({
        paymentId: `stripe`,
        status: 'error',
      }),
    );
  }
}
// #endregion

export function* initPayments() {
  yield put(paymentActions.setState({ status: 'pending', items: {} }));

  let data: PaymentAccount[] = yield call(getCachedPaymentsForCurrentUser);

  if (!data) {
    try {
      data = yield call(paymentApiServices.get);
      yield call(cachePayments, data);
    } catch (error) {
      // TODO:FATAL_ERROR
      put(paymentActions.setState({ status: 'none', items: {}, error }));
      return;
    }
  }

  const items = data.reduce((acc, p, idx) => {
    const overrides: Partial<WebOrder.State.Payment> & {
      paymentId: string;
      displayOrder: number;
      status: WebOrder.State.Payment['status'];
    } = {
      paymentId: `${p.PaymentAccountId}`,
      displayOrder: (idx + 1) * 10,
      status: 'success',
    };

    switch (p.PaymentAccountType) {
      case 'BrainTreePayPalPaymentAccount':
        // Add new paypal has token & no CustomerId
        if (p.TokenizationKey && !p.CustomerId) {
          overrides.paymentId = 'paypal';
          overrides.displayOrder = (data.length + 10) * 10; // move at the end
          overrides.status = 'pending';
          overrides.CanRemoveFromCustomerView = false;
        }
        break;
      case 'StripePayButtonPaymentAccount':
        overrides.paymentId = 'stripe';
        overrides.status = 'pending';
        overrides.Description = 'Loading';
        break;

      case GatewayPaymentAccount.Ideal:
      case GatewayPaymentAccount.Bancontact:
        // was like that before but comes from server 'true' 🤨
        overrides.SupportsSubmitOrderTips = false;
        break;

      default:
        break;
    }

    acc[overrides.paymentId] = { ...p, ...overrides };

    return acc;
  }, {} as WebOrder.State.Payments['items']);

  // inject new card option
  items.new_card = genNewCreditCardObject({
    displayOrder: (data.length + 1) * 10,
  });

  yield put(
    paymentActions.setState({
      status: 'success',
      items,
      error: undefined,
    }),
  );

  paymentLogger.debug('INITIALIZED');

  yield spawn(initPayPal);
}

export function* logoutCleanup() {
  paymentLogger.debug('LOGOUT_CLEANUP');
  yield put(paymentActions.setState({ status: 'none', items: {} }));
}

export function* watch() {
  yield takeEvery(paymentActions.setDefaultSelected, setDefaultSelected);
}

export function* watchPaymentChanges() {
  yield takeLeading(
    paymentActions.creditCardPaymentAdded,
    creditCardPaymentAdded,
  );
  yield takeEvery(paymentActions.paymentRemoved, paymentRemoved);
}

// LOGIN -> INIT_PAYMENTS -> PAYMENT_ACTIONS♻︎ -> LOGOUT
export function* paymentsFlow() {
  while (true) {
    yield take(userActions.loginSuccessBridge);

    yield call(initPayments);

    yield takeLeading(paymentActions.initializeStripe, initStripe);

    const watchTask = yield fork(watchPaymentChanges);

    // LOGOUT & CLEANUP
    yield take(userActions.logoutSuccessBridge);
    yield cancel(watchTask);
    yield fork(logoutCleanup);
  }
}

export function* paymentSaga() {
  yield all([fork(paymentsFlow), fork(watch)]);
}
